/**
 * Schedule Wizard — three steps to set up a recurring universe request.
 *
 * Step 1 — Who: recipient type (FDR / internal department), entity/department
 *          pick, named contact picker (typeahead).
 * Step 2 — What: consumer app (Audit/Scrubber or Reporting/Reporter),
 *          then either pick a protocol (with optional table subset) or pick
 *          reporting sections (with txt-only vs txt+census toggle).
 * Step 3 — When: name, cadence, start date, optional end date, review.
 */

import React, { useEffect, useState } from 'react';

const API = process.env.REACT_APP_API_URL;

const FREQ = [
  { id: 'weekly',    label: 'Weekly' },
  { id: 'biweekly',  label: 'Every 2 weeks' },
  { id: 'monthly',   label: 'Monthly' },
  { id: 'quarterly', label: 'Quarterly' },
];

const PERIOD_WINDOWS = [
  { id: 'previous_cycle',   label: 'Last cycle (recommended)' },
  { id: 'previous_week',    label: 'Previous calendar week' },
  { id: 'previous_month',   label: 'Previous calendar month' },
  { id: 'previous_quarter', label: 'Previous calendar quarter' },
  { id: 'previous_year',    label: 'Previous calendar year' },
];

export default function ScheduleWizard({ onCancel, onCreated }) {
  const [step, setStep] = useState(1);

  // Step 1 — Who
  const [recipientType, setRecipientType] = useState('fdr');
  const [fdrs, setFdrs] = useState([]);
  const [fdrId, setFdrId] = useState('');
  const [fdrContacts, setFdrContacts] = useState([]);
  const [selectedContactEmails, setSelectedContactEmails] = useState(new Set());

  const [departments, setDepartments] = useState([]);
  const [departmentId, setDepartmentId] = useState('');
  const [internalUsers, setInternalUsers] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());
  const [userSearch, setUserSearch] = useState('');
  // Also copy: anyone in the organization compliance wants on the email
  // (operator order 2026-09-10). The Compliance Officer is always copied.
  const [copyUsers, setCopyUsers] = useState([]);
  const [ccIds, setCcIds] = useState(new Set());
  const [ccSearch, setCcSearch] = useState('');

  // Step 2 — What
  const [picker, setPicker] = useState({ scrubber_protocols: [], reporter_sections: [] });
  const [consumerApp, setConsumerApp] = useState('scrubber');
  const [protocolId, setProtocolId] = useState('');
  const [allTables, setAllTables] = useState(true);
  const [selectedTables, setSelectedTables] = useState(new Set());
  const [selectedSections, setSelectedSections] = useState(new Set());
  const [bundleFlag, setBundleFlag] = useState('txt_only');

  // Step 3 — When
  const [name, setName] = useState('');
  const [frequency, setFrequency] = useState('quarterly');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reportingWindow, setReportingWindow] = useState('previous_cycle');
  const [ttlDays, setTtlDays] = useState(14);
  const [messageTemplate, setMessageTemplate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // ── Loaders ────────────────────────────────────────────────────────────
  useEffect(() => {
    fetch(`${API}/api/universe-requests/fdrs`, { credentials: 'include' })
      .then(r => r.json()).then(d => setFdrs(d.fdrs || []));
    fetch(`${API}/api/universe-requests/picker-catalog`, { credentials: 'include' })
      .then(r => r.json()).then(d => setPicker(d || { scrubber_protocols: [], reporter_sections: [] }));
    fetch(`${API}/api/departments`, { credentials: 'include' })
      .then(r => r.json()).then(d => setDepartments(d.departments || d || []))
      .catch(() => setDepartments([]));
  }, []);

  useEffect(() => {
    if (recipientType !== 'fdr' || !fdrId) { setFdrContacts([]); return; }
    fetch(`${API}/api/universe-requests/recipients/fdr-contacts?fdr_entity_id=${encodeURIComponent(fdrId)}`, { credentials: 'include' })
      .then(r => r.json()).then(d => setFdrContacts(d.contacts || []));
  }, [recipientType, fdrId]);

  useEffect(() => {
    const q = encodeURIComponent(ccSearch || '');
    fetch(`${API}/api/universe-requests/recipients/copy-users?q=${q}`, { credentials: 'include' })
      .then(r => r.json()).then(d => setCopyUsers(d.users || [])).catch(() => setCopyUsers([]));
  }, [ccSearch]);

  useEffect(() => {
    if (recipientType !== 'internal_department' || !departmentId) { setInternalUsers([]); return; }
    const q = encodeURIComponent(userSearch || '');
    fetch(`${API}/api/universe-requests/recipients/internal-users?department_id=${encodeURIComponent(departmentId)}&q=${q}`, { credentials: 'include' })
      .then(r => r.json()).then(d => setInternalUsers(d.users || []));
  }, [recipientType, departmentId, userSearch]);

  // ── Step gates ────────────────────────────────────────────────────────
  const canStep2 = (
    (recipientType === 'fdr' && fdrId && selectedContactEmails.size > 0) ||
    (recipientType === 'internal_department' && departmentId && selectedUserIds.size > 0)
  );

  const canStep3 = (
    (consumerApp === 'scrubber' && protocolId && (allTables || selectedTables.size > 0)) ||
    (consumerApp === 'reporter' && selectedSections.size > 0) ||
    (consumerApp === 'coa' && recipientType === 'internal_department')
  );

  const canSubmit = canStep3 && name.trim() && frequency && startDate && !submitting;

  // ── Submit ────────────────────────────────────────────────────────────
  async function submit() {
    setSubmitting(true); setError(null);
    try {
      const body = {
        name: name.trim(),
        recipient_type: recipientType,
        fdr_entity_id: recipientType === 'fdr' ? fdrId : null,
        internal_department_id: recipientType === 'internal_department' ? departmentId : null,
        recipient_user_ids: recipientType === 'internal_department' ? [...selectedUserIds] : [],
        // The FDR contacts picked above, by id; the entity's primary contact is
        // the fallback when none carries an id.
        recipient_contact_ids: recipientType === 'fdr' ? fdrContacts.filter(c => c.id && selectedContactEmails.has(c.email)).map(c => c.id) : [],
        cc_user_ids: [...ccIds],
        send_hour_local: 8,
        time_zone: 'America/New_York',
        consumer_app: consumerApp,
        scrubber_protocol_id: consumerApp === 'scrubber' ? protocolId : null,
        scrubber_table_ids: consumerApp === 'scrubber' && !allTables ? [...selectedTables] : null,
        reporter_section_ids: consumerApp === 'reporter' ? [...selectedSections] : null,
        reporter_bundle_flag: consumerApp === 'reporter' ? bundleFlag : null,
        frequency,
        start_date: startDate,
        end_date: endDate || null,
        reporting_period_window: reportingWindow,
        ttl_days: Number(ttlDays) || 14,
        message_template: messageTemplate || null,
      };
      const res = await fetch(`${API}/api/universe-requests/schedules`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Create failed');
      onCreated(data.schedule);
    } catch (e) {
      setError(e.message);
    } finally { setSubmitting(false); }
  }

  // ── Render helpers ─────────────────────────────────────────────────────
  const protocol = picker.scrubber_protocols.find(p => p.id === protocolId);

  return (
    <div className="max-w-4xl">
      <button onClick={onCancel} className="text-xs font-mono uppercase tracking-widest text-slate-500 hover:text-slate-900 mb-4">
        ← Back to schedules
      </button>

      <div className="mb-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-1">New recurring schedule</h2>
        <div className="text-sm text-slate-600">
          Send the same Universe Request to an FDR or internal department on a repeating cadence.
          The audit trail captures every cycle automatically — SOC 2 evidence builds itself.
        </div>
      </div>

      <ol className="flex gap-2 mb-6 text-xs font-mono uppercase tracking-widest">
        {[1, 2, 3].map(n => (
          <li key={n} className={`px-3 py-1 border ${step >= n ? 'border-red-700 text-red-700' : 'border-slate-300 text-slate-500'}`}>
            Step {n} — {n === 1 ? 'Who' : n === 2 ? 'What' : 'When'}
          </li>
        ))}
      </ol>

      {/* ───── Step 1 ─────────────────────────────────────────────────── */}
      {step === 1 && (
        <div className="bg-white border border-slate-200 p-6 space-y-5">
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Send to</label>
            <div className="grid grid-cols-2 gap-3">
              {[
                { id: 'fdr',                  label: 'An FDR',                 desc: 'External vendor — they receive a magic-link portal invite, MFA, upload there.' },
                { id: 'internal_department',  label: 'An internal department', desc: 'Health-plan staff — the email tells them to run the universe through Scrubber or Reporter, review it, and provide it to you at your work address. Never by reply.' },
              ].map(opt => (
                <label key={opt.id}
                  className={`block border p-3 cursor-pointer ${recipientType === opt.id ? 'border-red-700 bg-red-50' : 'border-slate-300 hover:border-slate-500'}`}>
                  <div className="flex items-center gap-2">
                    <input type="radio" name="recipient" checked={recipientType === opt.id} onChange={() => setRecipientType(opt.id)} />
                    <span className="text-sm font-semibold text-slate-900">{opt.label}</span>
                  </div>
                  <div className="text-xs text-slate-600 mt-1 pl-6">{opt.desc}</div>
                </label>
              ))}
            </div>
          </div>

          {recipientType === 'fdr' && (
            <>
              <div>
                <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">FDR entity</label>
                <select value={fdrId} onChange={e => { setFdrId(e.target.value); setSelectedContactEmails(new Set()); }} className="w-full border border-slate-300 px-2 py-1 text-sm">
                  <option value="">— Pick an FDR —</option>
                  {fdrs.map(f => <option key={f.id} value={f.id}>{f.fdr_name} ({f.contact_email})</option>)}
                </select>
                {fdrs.length === 0 && (
                  <div className="text-xs text-amber-700 mt-1">No FDRs registered yet. Add one under the <span className="font-semibold">FDRs</span> tab first.</div>
                )}
              </div>
              {fdrId && (
                <div>
                  <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">Contacts at this FDR (pick one or more)</label>
                  <div className="border border-slate-300 max-h-48 overflow-y-auto">
                    {fdrContacts.length === 0 && <div className="px-3 py-2 text-xs text-slate-500">Loading…</div>}
                    {fdrContacts.map(c => {
                      const key = c.email;
                      const isSel = selectedContactEmails.has(key);
                      return (
                        <label key={key} className={`flex items-center gap-2 px-3 py-2 cursor-pointer border-t border-slate-100 first:border-t-0 ${isSel ? 'bg-red-50' : 'hover:bg-slate-50'}`}>
                          <input type="checkbox" checked={isSel} onChange={() => {
                            const next = new Set(selectedContactEmails);
                            if (isSel) next.delete(key); else next.add(key);
                            setSelectedContactEmails(next);
                          }} />
                          <div className="flex-1">
                            <div className="text-sm text-slate-900">{c.name} <span className="text-xs text-slate-500">· {c.email}</span></div>
                            {c.title && <div className="text-[10px] font-mono uppercase tracking-wide text-slate-500">{c.title}{c.is_primary ? ' · primary' : ''}</div>}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}

          {recipientType === 'internal_department' && (
            <>
              <div>
                <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">Department</label>
                <select value={departmentId} onChange={e => { setDepartmentId(e.target.value); setSelectedUserIds(new Set()); }} className="w-full border border-slate-300 px-2 py-1 text-sm">
                  <option value="">— Pick a department —</option>
                  {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
              {departmentId && (
                <div>
                  <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">Who receives it (pick the department manager and their leader)</label>
                  <div className="text-xs text-slate-600 mb-2">The department's own people are listed first with the manager on top; the leader who oversees this department from outside it is listed as well.</div>
                  <input value={userSearch} onChange={e => setUserSearch(e.target.value)} placeholder="Search by name or email…"
                    className="w-full border border-slate-300 px-2 py-1 text-sm mb-2"/>
                  <div className="border border-slate-300 max-h-56 overflow-y-auto">
                    {internalUsers.length === 0 && <div className="px-3 py-2 text-xs text-slate-500">No users found.</div>}
                    {internalUsers.map(u => {
                      const isSel = selectedUserIds.has(u.id);
                      const tag = u.relationship === 'manager' ? 'Department manager' : u.relationship === 'leader' ? 'Leader · oversees this department' : null;
                      return (
                        <label key={u.id} className={`flex items-center gap-2 px-3 py-2 cursor-pointer border-t border-slate-100 first:border-t-0 ${isSel ? 'bg-red-50' : 'hover:bg-slate-50'}`}>
                          <input type="checkbox" checked={isSel} onChange={() => {
                            const next = new Set(selectedUserIds);
                            if (isSel) next.delete(u.id); else next.add(u.id);
                            setSelectedUserIds(next);
                          }} />
                          <div className="flex-1">
                            <div className="text-sm text-slate-900">{u.name} <span className="text-xs text-slate-500">· {u.email}</span></div>
                            <div className="text-[10px] font-mono uppercase tracking-wide text-slate-500">
                              {tag && <span className={`mr-2 px-1 py-px ${u.relationship === 'leader' ? 'bg-sky-50 text-sky-800' : 'bg-amber-50 text-amber-800'}`}>{tag}</span>}
                              {u.role}{u.department_name ? ` · ${u.department_name}` : ''}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}

          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">Also copy (optional)</label>
            <div className="text-xs text-slate-600 mb-2">
              Every email carries a copy to you as the requester, to the recipients' leader, and to the Compliance Officer. Add anyone else from the organization here.
            </div>
            {ccIds.size > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {copyUsers.filter(u => ccIds.has(u.id)).map(u => (
                  <span key={u.id} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-slate-100 text-slate-800">
                    {u.name || u.email}
                    <button type="button" onClick={() => { const n = new Set(ccIds); n.delete(u.id); setCcIds(n); }} className="text-slate-400 hover:text-slate-700">✕</button>
                  </span>
                ))}
              </div>
            )}
            <input value={ccSearch} onChange={e => setCcSearch(e.target.value)} placeholder="Search the organization by name or email…"
              className="w-full border border-slate-300 px-2 py-1 text-sm mb-2"/>
            <div className="border border-slate-300 max-h-40 overflow-y-auto">
              {copyUsers.filter(u => !ccIds.has(u.id) && !selectedUserIds.has(u.id)).slice(0, 25).map(u => (
                <button type="button" key={u.id} onClick={() => setCcIds(new Set([...ccIds, u.id]))}
                  className="w-full text-left flex items-center gap-2 px-3 py-1.5 border-t border-slate-100 first:border-t-0 hover:bg-slate-50">
                  <span className="text-sm text-slate-900">{u.name} <span className="text-xs text-slate-500">· {u.email}</span></span>
                  {u.is_compliance_officer && <span className="ml-auto text-[10px] font-mono uppercase tracking-wide text-slate-400">Compliance officer · always copied</span>}
                </button>
              ))}
            </div>
          </div>

          <div className="flex justify-end">
            <button disabled={!canStep2} onClick={() => setStep(2)}
              className="bg-red-700 hover:bg-red-800 disabled:bg-slate-300 text-white px-4 py-2 text-sm font-semibold">Next →</button>
          </div>
        </div>
      )}

      {/* ───── Step 2 ─────────────────────────────────────────────────── */}
      {step === 2 && (
        <div className="bg-white border border-slate-200 p-6 space-y-5">
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">What to request</label>
            <div className="grid grid-cols-3 gap-3">
              {[
                { id: 'scrubber', label: 'Audit protocol', desc: 'ODAG, CDAG, FA, SNPCC, CPE — pick a protocol and either all of its tables or just some.' },
                { id: 'reporter', label: 'Part C/D Reporting', desc: 'Section I–X, MTM, MPPP. For each section: TXT file alone, or TXT plus census report.' },
                { id: 'coa', label: 'Results entry (no universe)', desc: 'Reminds an internal department to enter its monthly results in the COA Work Plan. Lists the activities awaiting a result and closes itself when they are entered.' },
              ].map(opt => (
                <label key={opt.id}
                  className={`block border p-3 cursor-pointer ${consumerApp === opt.id ? 'border-red-700 bg-red-50' : 'border-slate-300 hover:border-slate-500'}`}>
                  <div className="flex items-center gap-2">
                    <input type="radio" name="consumer" checked={consumerApp === opt.id} onChange={() => setConsumerApp(opt.id)} />
                    <span className="text-sm font-semibold text-slate-900">{opt.label}</span>
                  </div>
                  <div className="text-xs text-slate-600 mt-1 pl-6">{opt.desc}</div>
                </label>
              ))}
            </div>
          </div>

          {consumerApp === 'scrubber' && (
            <>
              <div>
                <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">Protocol</label>
                <select value={protocolId} onChange={e => { setProtocolId(e.target.value); setSelectedTables(new Set()); setAllTables(true); }} className="w-full border border-slate-300 px-2 py-1 text-sm">
                  <option value="">— Pick a protocol —</option>
                  {picker.scrubber_protocols.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                </select>
              </div>
              {protocol && (
                <div>
                  <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">Tables in this protocol</label>
                  <div className="flex gap-3 mb-2 text-xs">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" checked={allTables} onChange={() => { setAllTables(true); setSelectedTables(new Set()); }}/>
                      All tables ({protocol.tables.length})
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" checked={!allTables} onChange={() => setAllTables(false)}/>
                      Specific tables
                    </label>
                  </div>
                  {!allTables && (
                    <div className="border border-slate-300">
                      {protocol.tables.map(t => {
                        const isSel = selectedTables.has(t.id);
                        return (
                          <label key={t.id} className={`flex items-center gap-2 px-3 py-2 cursor-pointer border-t border-slate-100 first:border-t-0 ${isSel ? 'bg-red-50' : 'hover:bg-slate-50'}`}>
                            <input type="checkbox" checked={isSel} onChange={() => {
                              const next = new Set(selectedTables);
                              if (isSel) next.delete(t.id); else next.add(t.id);
                              setSelectedTables(next);
                            }}/>
                            <span className="text-sm text-slate-900">{t.label}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {consumerApp === 'coa' && (
            <div className="border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
              No file is requested. On each run the email lists the department's Monitoring activities for the reporting period that have no universe behind them, and the request completes on its own once every one has a result in the COA Work Plan. Internal departments only.
            </div>
          )}

          {consumerApp === 'reporter' && (
            <>
              <div>
                <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Bundle</label>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { id: 'txt_only',         label: 'TXT file only',           desc: 'Just the HPMS submission .txt for each section.' },
                    { id: 'txt_plus_census',  label: 'TXT + census report',     desc: 'The .txt plus the underlying census / source-universe export (when the section has one).' },
                  ].map(opt => (
                    <label key={opt.id}
                      className={`block border p-3 cursor-pointer ${bundleFlag === opt.id ? 'border-red-700 bg-red-50' : 'border-slate-300 hover:border-slate-500'}`}>
                      <div className="flex items-center gap-2">
                        <input type="radio" name="bundle" checked={bundleFlag === opt.id} onChange={() => setBundleFlag(opt.id)} />
                        <span className="text-sm font-semibold text-slate-900">{opt.label}</span>
                      </div>
                      <div className="text-xs text-slate-600 mt-1 pl-6">{opt.desc}</div>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">Sections (pick one or more)</label>
                <div className="border border-slate-300 max-h-[40vh] overflow-y-auto">
                  {picker.reporter_sections.map(s => {
                    const isSel = selectedSections.has(s.id);
                    return (
                      <label key={s.id} className={`flex items-center gap-2 px-3 py-2 cursor-pointer border-t border-slate-100 first:border-t-0 ${isSel ? 'bg-red-50' : 'hover:bg-slate-50'}`}>
                        <input type="checkbox" checked={isSel} onChange={() => {
                          const next = new Set(selectedSections);
                          if (isSel) next.delete(s.id); else next.add(s.id);
                          setSelectedSections(next);
                        }}/>
                        <div className="flex-1">
                          <div className="text-sm text-slate-900">{s.label}</div>
                          <div className="text-[10px] font-mono uppercase tracking-wide text-slate-500">
                            Part {s.part}{!s.has_census && bundleFlag === 'txt_plus_census' ? ' · no census file available — TXT only for this section' : ''}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          <div className="flex justify-between">
            <button onClick={() => setStep(1)} className="text-sm text-slate-600 hover:text-slate-900">← Back</button>
            <button disabled={!canStep3} onClick={() => setStep(3)}
              className="bg-red-700 hover:bg-red-800 disabled:bg-slate-300 text-white px-4 py-2 text-sm font-semibold">Next →</button>
          </div>
        </div>
      )}

      {/* ───── Step 3 ─────────────────────────────────────────────────── */}
      {step === 3 && (
        <div className="bg-white border border-slate-200 p-6 space-y-4">
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">Schedule name</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g., Quarterly ODAG — Acme FDR"
              className="w-full border border-slate-300 px-2 py-1 text-sm"/>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">Cadence</label>
              <select value={frequency} onChange={e => setFrequency(e.target.value)} className="w-full border border-slate-300 px-2 py-1 text-sm">
                {FREQ.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">First send</label>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                className="w-full border border-slate-300 px-2 py-1 text-sm font-mono"/>
              <div className="text-[11px] text-slate-500 mt-1">Goes out at 8:00 AM Eastern on this date, and at 8:00 AM Eastern every cycle after.</div>
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">End date (optional)</label>
              <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                className="w-full border border-slate-300 px-2 py-1 text-sm font-mono"/>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">Reporting period each cycle</label>
              <select value={reportingWindow} onChange={e => setReportingWindow(e.target.value)} className="w-full border border-slate-300 px-2 py-1 text-sm">
                {PERIOD_WINDOWS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">Days to respond (TTL)</label>
              <input type="number" min={1} max={90} value={ttlDays} onChange={e => setTtlDays(e.target.value)}
                className="w-full border border-slate-300 px-2 py-1 text-sm font-mono"/>
            </div>
          </div>

          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">Message to recipient (optional)</label>
            <textarea value={messageTemplate} onChange={e => setMessageTemplate(e.target.value)} rows={3}
              placeholder="Plain English — what's this for, when's the deadline, anything they should know."
              className="w-full border border-slate-300 px-2 py-1 text-sm"/>
          </div>

          {error && <div className="border border-red-300 bg-red-50 text-red-800 px-3 py-2 text-sm font-mono">{error}</div>}

          <div className="bg-slate-50 border border-slate-200 px-4 py-3 text-xs text-slate-600">
            <div className="font-mono uppercase tracking-widest text-slate-500 mb-1">SOC 2 — Audit trail</div>
            Every cycle this schedule fires, every recipient invited, and every upload received chains into the immutable
            audit log. You can pause or disable the schedule any time without losing the historical record.
          </div>

          <div className="flex justify-between">
            <button onClick={() => setStep(2)} className="text-sm text-slate-600 hover:text-slate-900">← Back</button>
            <button disabled={!canSubmit} onClick={submit}
              className="bg-red-700 hover:bg-red-800 disabled:bg-slate-300 text-white px-6 py-2 text-sm font-semibold">
              {submitting ? 'Creating…' : 'Create schedule'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
